// src/pages/Assets.tsx

import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMultipleTokenPrices } from "@/hooks/use-dexscreener";
import { useAllBalances } from "@/hooks/use-all-balances";
import { CHAIN_IDS } from "@/lib/dexscreener";
import { ArrowUpRight, ArrowDownLeft, ChevronDown, ChevronUp } from "lucide-react";

type TokenBalance = {
  symbol: string;
  amount: number;
  balance: number;
  price: number;
  chain: string;
};

const SOLANA_TOKEN_ADDRESSES = {
  SOL: "So11111111111111111111111111111111111111112",
  USDT: "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB",
  USDC: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
  FIXERCOIN: "H4qKn8FMFha8jJuj8xMryMqRhH3h7GjLuxw7TVixpump",
  PINGX: "7KS4DgKHmgSWYC4uGnSozLUon2bDEj6WKhRNSosmpump",
  LOCKER: "EN1nYrW6375zMPUkpkGyGSEXW8WmAqYu4yhf6xnGpump",
  FXM: "7Fnx57ztmhdpL1uAGmUY1ziwPG2UDKmG6poB4ibjpump",
  JUP: "JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN",
  BONK: "DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263",
  PYTH: "Gnt27xtC473ZT2Mw5u8wZ68Z3gULkSTb5DuxJy7eJotD",
  ORCA: "orcaEKTdK7LKz57vaAYr9QeNsVEPfiu6QeMU1kektZE",
  RAY: "4k3Dyjzvzp8eMZWUXbBCjEvwSkkk59S5iCNLY3QrkX6R",
  JTO: "jtojtomepa8beP8AuQc6eXt5FriJwfFMwQx2v2f9mCL"
};

const EVM_TOKENS = [
  { symbol: "ETH", chain: "ethereum" }, { symbol: "USDT", chain: "ethereum" }, { symbol: "USDC", chain: "ethereum" },
  { symbol: "DAI", chain: "ethereum" }, { symbol: "UNI", chain: "ethereum" }, { symbol: "LINK", chain: "ethereum" },
  { symbol: "BTC", chain: "ethereum" }, { symbol: "BNB", chain: "bsc" }, { symbol: "CAKE", chain: "bsc" },
  { symbol: "USDT", chain: "bsc" },
  { symbol: "MATIC", chain: "polygon" }, { symbol: "TRX", chain: "tron" }, { symbol: "USDT", chain: "tron" },
  { symbol: "SHIB", chain: "ethereum" },
];

const PORTFOLIO_TOKENS = [
  ...Object.keys(SOLANA_TOKEN_ADDRESSES).map(symbol => ({ symbol, address: SOLANA_TOKEN_ADDRESSES[symbol as keyof typeof SOLANA_TOKEN_ADDRESSES], chain: "solana" })),
  ...EVM_TOKENS.map(token => ({ symbol: token.symbol, address: token.symbol, chain: token.chain })),
];

const TOKEN_CHAINS: Record<string, string> = {
  SOL: "solana", USDT: "solana", USDC: "solana", FIXERCOIN: "solana", PINGX: "solana", LOCKER: "solana", FXM: "solana",
  JUP: "solana", BONK: "solana", PYTH: "solana", ORCA: "solana", RAY: "solana", JTO: "solana", FIDA: "solana",
  ETH: "ethereum", BNB: "bsc", MATIC: "polygon", BTC: "ethereum", DAI: "ethereum", UNI: "ethereum", LINK: "ethereum", CAKE: "bsc",
  TRX: "tron",
  SHIB: "ethereum",
};

const CHAIN_DISPLAY_NAMES: Record<string, string> = {
  solana: "SOLANA", ethereum: "ETHEREUM", bsc: "BSC", polygon: "POLYGON", tron: "TRON",
};

const INITIAL_TOKEN_BALANCES: TokenBalance[] = PORTFOLIO_TOKENS.map(token => ({
  symbol: token.symbol, amount: 0, balance: 0, price: 0, chain: token.chain,
}));

// ========== Currency Selector (same mechanism as Dashboard) ==========
const CURRENCIES = [
  { code: "USD", symbol: "$", label: "US DOLLAR" },
  { code: "PKR", symbol: "₨", label: "PAKISTANI RUPEE" },
  { code: "EUR", symbol: "€", label: "EURO" },
  { code: "GBP", symbol: "£", label: "BRITISH POUND" },
  { code: "INR", symbol: "₹", label: "INDIAN RUPEE" },
  { code: "AED", symbol: "د.إ", label: "UAE DIRHAM" },
  { code: "SAR", symbol: "﷼", label: "SAUDI RIYAL" },
  { code: "JPY", symbol: "¥", label: "JAPANESE YEN" },
  { code: "CNY", symbol: "¥", label: "CHINESE YUAN" },
  { code: "BTC", symbol: "₿", label: "BITCOIN" },
] as const;

const FALLBACK_FX: Record<string, number> = {
  USD: 1,
  PKR: 278,
  EUR: 0.92,
  GBP: 0.79,
  INR: 83,
  AED: 3.67,
  SAR: 3.75,
  JPY: 150,
  CNY: 7.2,
  BTC: 1 / 65000,
};

const BTC_ADDRESS = "0x2260fac5e5542a773aa44fbcfedf7c193bc2c599";

const convertValue = (
  usdValue: number,
  currencyCode: string,
  prices?: Record<string, any>
): number => {
  if (!usdValue) return 0;
  if (currencyCode === "USD") return usdValue;
  if (currencyCode === "BTC") {
    const btcPrice = prices?.[BTC_ADDRESS]?.priceUsd || 65000;
    return usdValue / btcPrice;
  }
  return usdValue * (FALLBACK_FX[currencyCode] || 1);
};

// No symbol prefix — currency is shown in the label, not on the number
const formatCurrencyValue = (value: number, currencyCode: string): string => {
  if (currencyCode === "BTC") return value.toFixed(8);
  if (currencyCode === "PKR" || currencyCode === "INR" || currencyCode === "JPY") {
    return value.toLocaleString("en-US", { maximumFractionDigits: 0 });
  }
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const ShortcutDetails = ({ userEmail }: { userEmail?: string }) => (
  <div className="bg-card rounded-xl p-6 border border-border h-full">
    <div className="flex items-center gap-2 mb-6">
      <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
        <span className="text-black text-[10px] font-bold">→</span>
      </div>
      <h2 className="text-[11px] font-bold uppercase tracking-wider text-yellow-400">Assets Info</h2>
    </div>
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">YOUR ACCOUNT</p>
        <p className="text-[11px] font-mono text-foreground break-all">{userEmail || "Not logged in"}</p>
      </div>
      <div className="border-t border-border my-2"></div>
      <div className="space-y-2">
        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">ACTIONS</p>
        <ul className="space-y-1">
          <li className="text-[11px] text-muted-foreground uppercase flex items-start gap-1">
            <span className="text-yellow-400">•</span>
            <span>Deposit funds to increase balance</span>
          </li>
          <li className="text-[11px] text-muted-foreground uppercase flex items-start gap-1">
            <span className="text-yellow-400">•</span>
            <span>Withdraw tokens to external wallet</span>
          </li>
          <li className="text-[11px] text-muted-foreground uppercaseflex items-start gap-1">
            <span className="text-yellow-400">•</span>
            <span>Click token card to trade</span>
          </li>
        </ul>
      </div>
      <div className="border-t border-border my-2"></div>
      <div className="space-y-2">
        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground uppercase">SUPPORT</p>
        <p className="text-[11px] text-muted-foreground uppercase">Need help with your assets? Contact support</p>
      </div>
    </div>
  </div>
);
export default function Assets() {
  const [user, setUser] = useState<any | null>(null);
  const [tokenBalances, setTokenBalances] = useState<TokenBalance[]>(INITIAL_TOKEN_BALANCES);
  const [tokenPrices, setTokenPrices] = useState<Record<string, number>>({});
  const [hasInitialData, setHasInitialData] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [showAllTokens, setShowAllTokens] = useState(false);
  const navigate = useNavigate();

  // ===== Currency state (same mechanism as Dashboard) =====
  const [currency, setCurrency] = useState<string>("USD");
  const [showCurrencyMenu, setShowCurrencyMenu] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("dashboard_currency");
    if (stored && CURRENCIES.some((c) => c.code === stored)) {
      setCurrency(stored);
    }
  }, []);
  useEffect(() => {
    localStorage.setItem("dashboard_currency", currency);
  }, [currency]);

  useEffect(() => {
    if (!showCurrencyMenu) return;
    const handler = () => setShowCurrencyMenu(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [showCurrencyMenu]);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch("/api/auth", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            setUser(data.user);
            return;
          }
        }
        navigate("/login");
      } catch (error) {
        console.error("[Assets] Auth error:", error);
        navigate("/login");
      }
    };
    fetchUser();
  }, [navigate]);

  const solanaWalletAddress = user?.wallets?.solana || "";
  const { solanaBalances, evmBalances, loading: balancesLoading, refreshing: balancesRefreshing, error: balancesError } = useAllBalances({
    solanaWalletAddress,
    userId: user?.email || null,
    refetchInterval: 60000,
    enabled: !!user?.email,
  });

  const solanaTokenAddresses = useMemo(() => Object.values(SOLANA_TOKEN_ADDRESSES), []);
  const { prices: solanaPrices, loading: pricesLoading } = useMultipleTokenPrices(solanaTokenAddresses, { refetchInterval: 60000 });

  // Also fetch BTC price so currency conversion to BTC works (same as Dashboard uses prices[BTC_ADDRESS])
  const btcPriceAddresses = useMemo(() => [BTC_ADDRESS], []);
  const { prices: btcPrices } = useMultipleTokenPrices(btcPriceAddresses, { refetchInterval: 60000 });
  const conversionPrices = useMemo(
    () => ({ ...(solanaPrices || {}), ...(btcPrices || {}) }),
    [solanaPrices, btcPrices]
  );

  const getTokenPrice = useCallback((symbol: string, chain: string): number => {
    if (symbol === "USDT" || symbol === "USDC" || symbol === "DAI") return 1;
    if (chain === "solana") {
      const address = SOLANA_TOKEN_ADDRESSES[symbol as keyof typeof SOLANA_TOKEN_ADDRESSES];
      if (address && solanaPrices?.[address]) return solanaPrices[address].priceUsd;
    }
    const fallbackPrices: Record<string, number> = { ETH: 3500, BNB: 600, MATIC: 0.8, USDT: 1, USDC: 1, DAI: 1, UNI: 7, LINK: 15, BTC: 65000, CAKE: 2.5 };
    return fallbackPrices[symbol] || 0;
  }, [solanaPrices]);

  useEffect(() => {
    const updateAllBalances = () => {
      const updatedBalances = PORTFOLIO_TOKENS.map(token => {
        let amount = 0;
        if (token.chain === "solana") {
          amount = solanaBalances?.[token.symbol] ?? 0;
        } else {
          let rawAmount = evmBalances?.[token.chain]?.[token.symbol] ?? 0;
          if (token.symbol === "USDT" && token.chain === "bsc") {
            rawAmount = rawAmount / 1e18;
          }
          amount = rawAmount;
        }
        const price = getTokenPrice(token.symbol, token.chain);
        const usdBalance = amount * price;
        return { symbol: token.symbol, amount, balance: usdBalance, price, chain: token.chain };
      });
      setTokenBalances(updatedBalances);
      const newPrices: Record<string, number> = {};
      PORTFOLIO_TOKENS.forEach(token => newPrices[token.symbol] = getTokenPrice(token.symbol, token.chain));
      setTokenPrices(newPrices);
      setHasInitialData(true);
    };
    if (solanaBalances || evmBalances) updateAllBalances();
  }, [solanaBalances, evmBalances, getTokenPrice]);

  const totalBalance = useMemo(() => tokenBalances.reduce((sum, t) => sum + (t.balance > 0 ? t.balance : 0), 0), [tokenBalances]);
  const tokensWithBalance = useMemo(() => tokenBalances.filter(t => t.amount > 0), [tokenBalances]);

  const sortedTokens = useMemo(() => {
    return [...tokensWithBalance].sort((a, b) => b.balance - a.balance);
  }, [tokensWithBalance]);

  const tokensByChain = useMemo(() => {
    const grouped: Record<string, TokenBalance[]> = {};
    tokensWithBalance.forEach(token => {
      const chain = token.chain;
      if (!grouped[chain]) grouped[chain] = [];
      grouped[chain].push(token);
    });
    return grouped;
  }, [tokensWithBalance]);

  const visibleTokens = showAllTokens ? sortedTokens : sortedTokens.slice(0, 4);
  const hasMore = sortedTokens.length > 4;

  const formatTokenAmount = (amount: number) => {
    if (amount === 0) return "0.000";
    if (amount < 0.001) return amount.toExponential(4);
    if (amount < 1) return amount.toFixed(6);
    return amount.toLocaleString("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  };
  const formatPrice = (price: number) => {
    if (price <= 0) return "---";
    if (price < 0.001) return `$${price.toFixed(6)}`;
    if (price < 1) return `$${price.toFixed(4)}`;
    return `$${price.toFixed(2)}`;
  };
  const isLoading = (!hasInitialData && (pricesLoading || balancesLoading)) && tokensWithBalance.length === 0;

  // ===== Currency conversion helpers (same as Dashboard) =====
  const convertedTotal = convertValue(totalBalance, currency, conversionPrices);
  const activeCurrency = CURRENCIES.find((c) => c.code === currency) || CURRENCIES[0];

  // Converted USD balance for a single token row (used where $ value is displayed)
  const convertRowUsd = (usd: number) => convertValue(usd, currency, conversionPrices);

  // Currency selector dropdown (identical markup to Dashboard)
  const CurrencySelector = () => (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setShowCurrencyMenu((v) => !v)}
        className="flex items-center gap-1 px-2 py-1 rounded-md border border-border bg-background hover:bg-muted transition-colors"
        aria-label="Select currency"
        title={`Currency: ${activeCurrency.code}`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-foreground"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
        <span className="text-[10px] font-semibold uppercase text-foreground">
          {currency}
        </span>
      </button>

      {showCurrencyMenu && (
        <div className="absolute right-0 mt-1 w-44 max-h-64 overflow-y-auto bg-card border border-border rounded-md shadow-lg z-50">
          {CURRENCIES.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => {
                setCurrency(c.code);
                setShowCurrencyMenu(false);
              }}
              className={`w-full text-left px-3 py-2 text-[11px] uppercase transition-colors hover:bg-muted ${
                c.code === currency
                  ? "text-yellow-400 font-semibold"
                  : "text-foreground"
              }`}
            >
              <span className="font-bold">{c.code}</span>
              <span className="ml-2 text-muted-foreground">{c.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
    const AssetsContent = () => (
    <div className="bg-card rounded-xl p-6 border border-border h-full flex flex-col">
      <div className="space-y-6 mb-8">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[14px] text-muted-foreground tracking-wide uppercase">
              TOTAL BALANCE ({currency})
            </div>
            <CurrencySelector />
          </div>
          <div className="text-5xl font-bold text-foreground">
            {formatCurrencyValue(convertedTotal, currency)}
          </div>
        </div>
      </div>
      <Tabs defaultValue="assets" className="w-full flex-1 flex flex-col">
        <TabsList className="grid w-full grid-cols-3 bg-transparent border-b border-border rounded-none h-auto p-0 gap-8">
          <TabsTrigger value="assets" className="bg-transparent border-b-2 border-transparent data-[state=active]:border-yellow-400 data-[state=active]:bg-transparent rounded-none px-0 py-3 text-[10px] font-semibold uppercase text-muted-foreground data-[state=active]:text-yellow-400">MY ASSETS</TabsTrigger>
          <TabsTrigger value="deposit" className="bg-transparent border-b-2 border-transparent data-[state=active]:border-yellow-400 data-[state=active]:bg-transparent rounded-none px-0 py-3 text-[10px] font-semibold uppercase text-muted-foreground data-[state=active]:text-yellow-400" onClick={() => navigate("/deposit")}>DEPOSIT</TabsTrigger>
          <TabsTrigger value="withdraw" className="bg-transparent border-b-2 border-transparent data-[state=active]:border-yellow-400 data-[state=active]:bg-transparent rounded-none px-0 py-3 text-[10px] font-semibold uppercase text-muted-foreground data-[state=active]:text-yellow-400" onClick={() => navigate("/withdraw")}>WITHDRAW</TabsTrigger>
        </TabsList>
        <TabsContent value="assets" className="space-y-4 mt-6 flex-1">
          {isLoading && tokensWithBalance.length === 0 ? (
            <div className="space-y-3">{[...Array(5)].map((_,i) => <div key={i} className="flex items-center justify-between p-4 rounded-lg border border-border/30 animate-pulse"><div className="flex flex-col gap-1"><div className="h-4 bg-muted rounded w-16" /><div className="h-3 bg-muted rounded w-12" /><div className="h-4 bg-muted rounded w-24" /></div></div>)}</div>
          ) : sortedTokens.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-[10px] uppercase">NO TOKENS IN PORTFOLIO</div>
          ) : (
            <div className="space-y-4">
              {visibleTokens.map(token => (
                <div key={token.symbol} className="grid grid-cols-3 gap-4 py-3 rounded-lg hover:bg-muted transition-colors cursor-pointer" onClick={() => navigate("/markets")}>
                  <div>
                    <div className="font-semibold text-[10px] uppercase text-foreground">{token.symbol}</div>
                    <div className="text-[8px] text-muted-foreground">{formatPrice(token.price)}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-[10px] text-foreground">{formatTokenAmount(token.amount)}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-[10px] text-foreground">{formatCurrencyValue(convertRowUsd(token.balance), currency)}</div>
                  </div>
                </div>
              ))}
              {hasMore && (
                <>
                  <div className="border-t border-border my-2"></div>
                  <button
                    onClick={() => setShowAllTokens(!showAllTokens)}
                    className="w-full flex items-center justify-center gap-2 text-[9px] uppercase text-yellow-400 hover:text-yellow-500 transition py-2"
                  >
                    {showAllTokens ? (
                      <>SHOW LESS <ChevronUp className="w-3 h-3" /></>
                    ) : (
                      <>MORE TOKENS <ChevronDown className="w-3 h-3" /></>
                    )}
                  </button>
                </>
              )}
            </div>
          )}
        </TabsContent>
        <TabsContent value="deposit" className="space-y-4 mt-6">
          <div className="flex flex-col gap-3">
            <Button onClick={() => navigate("/deposit")} className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-semibold py-6 text-[10px] uppercase">
              <ArrowDownLeft className="w-4 h-4 mr-2" />GO TO DEPOSIT
            </Button>
            <p className="text-[8px] text-muted-foreground text-center uppercase">Click above to deposit funds into your account</p>
          </div>
        </TabsContent>
        <TabsContent value="withdraw" className="space-y-4 mt-6">
          <div className="flex flex-col gap-3">
            <Button onClick={() => navigate("/withdraw")} className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-semibold py-6 text-[10px] uppercase">
              <ArrowUpRight className="w-4 h-4 mr-2" />GO TO WITHDRAW
            </Button>
            <p className="text-[8px] text-muted-foreground text-center uppercase">Click above to withdraw funds from your account</p>
          </div>
        </TabsContent>
      </Tabs>
      {!hasInitialData && pricesLoading && tokensWithBalance.length > 0 && (
        <div className="fixed bottom-4 right-4 flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2">
          <div className="w-3 h-3 rounded-full border-2 border-yellow-400 border-t-transparent animate-spin" />
          <span className="text-[8px] text-muted-foreground uppercase">Updating prices...</span>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      {isDesktop ? (
        <div className="flex gap-[3px] p-6 min-h-screen">
          <div className="w-1/2"><ShortcutDetails userEmail={user?.email} /></div>
          <div className="w-1/2"><AssetsContent /></div>
        </div>
      ) : (
        <div className="px-2 py-8">
          <div className="space-y-6 mb-8">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground tracking-wide">
                  TOTAL BALANCE ({currency})
                </div>
                <CurrencySelector />
              </div>
              <div className="text-5xl font-bold text-foreground">
                {formatCurrencyValue(convertedTotal, currency)}
              </div>
            </div>
          </div>
          <Tabs defaultValue="assets" className="w-full">
            <TabsList className="grid w-full grid-cols-3 bg-transparent border-b border-border rounded-none h-auto p-0 gap-8">
              <TabsTrigger value="assets" className="bg-transparent border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent rounded-none px-0 py-3 text-sm font-semibold uppercase text-muted-foreground data-[state=active]:text-primary">MY ASSETS</TabsTrigger>
              <TabsTrigger value="deposit" className="bg-transparent border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent rounded-none px-0 py-3 text-sm font-semibold uppercase text-muted-foreground data-[state=active]:text-primary" onClick={() => navigate("/deposit")}>DEPOSIT</TabsTrigger>
              <TabsTrigger value="withdraw" className="bg-transparent border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent rounded-none px-0 py-3 text-sm font-semibold uppercase text-muted-foreground data-[state=active]:text-primary" onClick={() => navigate("/withdraw")}>WITHDRAW</TabsTrigger>
            </TabsList>
            <TabsContent value="assets" className="space-y-4 mt-6">
              {isLoading && tokensWithBalance.length === 0 ? (
                <div className="space-y-3">{[...Array(5)].map((_,i) => <div key={i} className="flex items-center justify-between p-4 rounded-lg border border-border/30 animate-pulse"><div className="flex flex-col gap-1"><div className="h-4 bg-muted rounded w-16" /><div className="h-3 bg-muted rounded w-12" /><div className="h-4 bg-muted rounded w-24" /></div></div>)}</div>
              ) : tokensWithBalance.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">NO TOKENS IN PORTFOLIO</div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(tokensByChain).map(([chain, tokens]) => tokens.length > 0 && (
                    <div key={chain} className="space-y-3">
                      <div className="flex items-center gap-2 pb-2 border-b border-border">
                        <div className="w-2 h-2 rounded-full bg-primary" />
                        <div className="text-xs font-bold uppercase text-primary tracking-wider">{CHAIN_DISPLAY_NAMES[chain] || chain.toUpperCase()}</div>
                      </div>
                      {tokens.map(token => (
                        <div key={token.symbol} className="flex items-center justify-between p-4 rounded-lg border border-border/30 hover:border-primary/50 transition-colors cursor-pointer" onClick={() => navigate("/markets")}>
                          <div>
                            <div className="font-semibold text-sm text-foreground">{token.symbol}</div>
                            <div className="text-xs text-muted-foreground">{formatPrice(token.price)}</div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-sm text-foreground">{formatTokenAmount(token.amount)}</div>
                            <div className="text-xs text-muted-foreground">≈ {formatCurrencyValue(convertRowUsd(token.balance), currency)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
            <TabsContent value="deposit" className="space-y-4 mt-6">
              <div className="flex flex-col gap-3">
                <Button onClick={() => navigate("/deposit")} className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-semibold py-6">DEPOSIT</Button>
                <p className="text-sm text-muted-foreground text-center">Click above to deposit funds into your account</p>
              </div>
            </TabsContent>
            <TabsContent value="withdraw" className="space-y-4 mt-6">
              <div className="flex flex-col gap-3">
                <Button onClick={() => navigate("/withdraw")} className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-semibold py-6">WITHDRAW</Button>
                <p className="text-sm text-muted-foreground text-center">Click above to withdraw funds from your account</p>
              </div>
            </TabsContent>
          </Tabs>
          {!hasInitialData && pricesLoading && tokensWithBalance.length > 0 && (
            <div className="fixed bottom-4 right-4 flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2">
              <div className="w-3 h-3 rounded-full border-2 border-primary border-t-transparent animate-spin" />
              <span className="text-xs text-muted-foreground">Updating prices...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
